import {
    ApplicationFilters,
    SavedFilter,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import BaseService from "./BaseService";
import { SafetyEventType } from "stores/ApplicationFilters/Filters/SafetyEvent";
import { SubscriptionCadence } from "components/Conversations/Views/Drawer/components/Subscriptions/helpers";

// export type ApplicationFilterDomain =
//     | "Conversations" // conversations domain only filter
//     | "Signals" // signals domain only filter
//     | "User" // non-domain specific user only filter
//     | "Organization"; // tenant / org wide filter;

export enum ApplicationFilterDomain {
    /**
     * conversations domain only filter
     * @deprecated signals and conversations now support the same filter set. Use `User` instead.
     */
    Conversations = "Conversations",
    /**
     * signals domain only filter
     * @deprecated signals and conversations now support the same filter set. Use `User` instead.
     */
    Signals = "Signals",
    /**
     * non-domain specific user only filter
     */
    User = "User",
    /**
     * tenant / org wide filter
     */
    Organization = "Organization",
}

export interface CreateApplicationFilter {
    name: string;
    value: ApplicationFilters;
    domain: ApplicationFilterDomain;
    subscriptionInfo?: SubscriptionInfoRequest;
}

export interface SubscriptionInfoRequest {
    filterHtml: string;
    cadence: SubscriptionCadence;
    name: string;
    isKboAnalysisEnabled?: boolean;
    kboAnalysisPrompt?: string | null;
}

export interface UpdateApplicationFilter {
    id: string;
    name?: string;
    value?: ApplicationFilters;
    domain?: string;
    filterHtml?: string;
    applicationFilterSubscriptionId?: string;
}

export interface ApplicationFilterSubscription {
    applicationFilterId: string;
    cadence: SubscriptionCadence;
    id: string;
    isActive: boolean;
    isKboAnalysisEnabled: boolean;
    kboAnalysisPrompt: string | null;
}

type UnparsedSavedFilter = Omit<SavedFilter, "value"> & { value: string };

export class ApplicationFiltersService extends BaseService {
    protected urlBase = "api/ApplicationFilter";

    public async willReceiveFullData(value: ApplicationFilters) {
        const apiPath = "WillReceiveFullData";
        const urlParams = new URLSearchParams();

        return this.post<boolean>(JSON.stringify(value), urlParams, apiPath);
    }

    public async createApplicationFilter(
        filter: CreateApplicationFilter,
    ): Promise<SavedFilter> {
        const apiPath = "CreateApplicationFilter";
        const urlParams = new URLSearchParams();
        const body = {
            name: filter.name,
            domain: filter.domain,
            value: JSON.stringify(filter.value),
        };

        const createdFilter = await this.post<SavedFilter>(
            JSON.stringify(body),
            urlParams,
            apiPath,
        );
        createdFilter.value = JSON.parse(
            createdFilter.value as unknown as string,
        );
        return createdFilter;
    }

    // Add here somehow
    public async updateApplicationFilter(filter: UpdateApplicationFilter) {
        const apiPath = "UpdateApplicationFilter";
        const urlParams = new URLSearchParams();
        const body = {
            ...filter,
            value: JSON.stringify(filter.value),
        };

        return this.put<void>(urlParams, apiPath, JSON.stringify(body));
    }

    public async updateApplicationFilters(filters: UpdateApplicationFilter[]) {
        const apiPath = "UpdateApplicationFilters";
        const urlParams = new URLSearchParams();

        const body = filters.map((filter) => ({
            ...filter,
            value: filter.value ? JSON.stringify(filter.value) : undefined,
        }));

        return this.put<void>(urlParams, apiPath, JSON.stringify(body));
    }

    public async deleteApplicationFilter(id: string) {
        const apiPath = "DeleteApplicationFilter";
        const urlParams = new URLSearchParams({ id });

        return this.delete(urlParams, apiPath);
    }

    public async deleteApplicationFilters(ids: string[]) {
        const apiPath = "DeleteApplicationFilters";
        const urlParams = new URLSearchParams();
        const body = ids;

        return this.delete(urlParams, apiPath, JSON.stringify(body));
    }

    public async getApplicationFiltersForDomain(
        domain: ApplicationFilterDomain,
    ) {
        const apiPath = "GetApplicationFilterForDomain";
        const urlParams = new URLSearchParams({ domain });

        const unparsed = await this.get<UnparsedSavedFilter[]>(
            urlParams,
            apiPath,
        );

        const parsed: SavedFilter[] = [];

        for (const filter of unparsed)
            parsed.push({ ...filter, value: JSON.parse(filter.value) });

        return parsed;
    }

    public async getApplicationFiltersByDomain() {
        const apiPath = "GetApplicationFiltersByDomain";
        const urlParams = new URLSearchParams();

        const unparsed = await this.get<
            {
                domain: ApplicationFilterDomain;
                filters: UnparsedSavedFilter[];
            }[]
        >(urlParams, apiPath);

        const parsedByDomain: Partial<
            Record<ApplicationFilterDomain, SavedFilter[]>
        > = {};

        for (const value of unparsed) {
            const parsedForDomain: SavedFilter[] = [];

            for (const filter of value.filters) {
                parsedForDomain.push({
                    ...filter,
                    value: JSON.parse(filter.value),
                });
            }

            parsedByDomain[value.domain] = parsedForDomain;
        }

        return parsedByDomain;
    }

    public async getSafetyEventTypes(): Promise<SafetyEventType[]> {
        const apiPath = "GetSafetyEventTypes";

        return this.get(new URLSearchParams({}), apiPath);
    }

    public async createApplicationFilterSubscription(
        filter: CreateApplicationFilter,
    ): Promise<SavedFilter> {
        const apiPath = "CreateApplicationFilterSubscription";
        const urlParams = new URLSearchParams();
        const body = {
            name: filter.name,
            domain: filter.domain,
            value: JSON.stringify(filter.value),
            subscriptionInfo: filter.subscriptionInfo,
        };

        const response = await this.post(
            JSON.stringify(body),
            urlParams,
            apiPath,
        );
        return {
            ...response,
            value: JSON.parse(response.value),
        };
    }

    public async createApplicationFilterSubscriptionFromSavedFilter(
        subscriptionInfo: SubscriptionInfoRequest,
        applicationFilterId: string,
    ) {
        const apiPath = "CreateApplicationFilterSubscriptionFromSavedFilter";
        const urlParams = new URLSearchParams({ applicationFilterId });

        return this.post<string>(
            JSON.stringify(subscriptionInfo),
            urlParams,
            apiPath,
        );
    }

    public async getApplicationFilterSubscription(
        applicationFilterSubscriptionId: string,
    ): Promise<ApplicationFilterSubscription> {
        const apiPath = "GetApplicationFilterSubscription";

        return this.get(
            new URLSearchParams({ applicationFilterSubscriptionId }),
            apiPath,
        );
    }

    public async updateApplicationFilterSubscription(
        applicationFilterSubscriptionId: string,
        request: {
            cadence: SubscriptionCadence;
            isKboAnalysisEnabled: boolean;
            kboAnalysisPrompt: string | null;
        },
    ) {
        const apiPath = "UpdateApplicationFilterSubscription";
        const urlParams = new URLSearchParams({
            applicationFilterSubscriptionId,
        });

        return this.put<string>(urlParams, apiPath, JSON.stringify(request));
    }

    public async unsubscribeFromApplicationFilter(
        applicationFilterSubscriptionId: string,
    ) {
        const apiPath = "UnsubscribeFromApplicationFilter";
        const urlParams = new URLSearchParams({
            applicationFilterSubscriptionId,
        });

        return this.delete(urlParams, apiPath);
    }

    public async sendImmediateEmail(subscriptionId: string) {
        const apiPath = "SendImmediateEmail";
        const urlParams = new URLSearchParams({ subscriptionId });

        return this.post<{ message: string }>(
            undefined,
            urlParams,
            apiPath,
        );
    }
}
